import React, { useCallback, useState } from 'react';
import ReactVisibilitySensor from 'react-visibility-sensor';
import { ApolloQueryResult, FetchMoreQueryOptions } from '@apollo/client';

import ChatMessage from 'buyside/components/presentation/ChatLayout/Chat/ChatMessages/chatMessage';
import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import SystemChatMessages from 'buyside/components/presentation/ChatLayout/Chat/ChatMessages/systemChatMessages';

import { ChatQuery, ChatQueryVariables, Maybe } from 'interfaces/graphql-typings';

import styles from './chatMessagesStyles.scss';

const ChatMessages: React.FC<{
  canManageMessages: boolean;
  canViewIdeas: boolean;
  chatMessages: Maybe<NonNullable<ChatQuery['chat']>['chatMessages']>;
  chatMessagesCount: number;
  currentUserId: string;
  fetchMore: (
    fetchMoreOptions: FetchMoreQueryOptions<ChatQueryVariables, ChatQuery>
  ) => Promise<ApolloQueryResult<ChatQuery>>;
}> = ({
  canManageMessages,
  canViewIdeas,
  chatMessages,
  chatMessagesCount,
  currentUserId,
  fetchMore
}) => {
  const [loadingMore, setLoadingMore] = useState(false);

  const { endCursor, hasNextPage } = chatMessages?.pageInfo || {};

  const handleLoadMore = useCallback(() => {
    if (loadingMore) return;

    setLoadingMore(true);

    fetchMore({
      variables: {
        cursor: endCursor
      }
    }).then(() => setLoadingMore(false));
  }, [endCursor, fetchMore, loadingMore]);

  return (
    <div className={styles.chatMessages}>
      <SystemChatMessages
        canManageMessages={canManageMessages}
        canViewIdeas={canViewIdeas}
        chatMessagesCount={chatMessagesCount}
      />
      {chatMessages?.edges?.map((edge, idx) => {
        const chatMessage = edge?.node;

        if (!chatMessage) return null;

        const previousChatMessage =
          idx + 1 === chatMessages.edges?.length ? null : (chatMessages.edges || [])[idx + 1]?.node;

        return (
          <ChatMessage
            key={`chat-message-${chatMessage.id}`}
            belongsToCurrentUser={chatMessage.user.id === currentUserId}
            chatMessage={chatMessage}
            hideUserInfo={
              chatMessage.user.id === previousChatMessage?.user.id &&
              Boolean(chatMessage.anonymous) === Boolean(previousChatMessage?.anonymous) &&
              chatMessage.direction === previousChatMessage.direction
            }
          />
        );
      })}
      {hasNextPage ? (
        <ReactVisibilitySensor
          onChange={(visible: boolean): void => {
            if (visible) handleLoadMore();
          }}
        >
          <LoadingSpinner />
        </ReactVisibilitySensor>
      ) : null}
    </div>
  );
};

export default ChatMessages;
